import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton,
  MenuItem, Select, Stack, Typography,
} from '@mui/material';
import Iconify from '../iconify';

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

function scopeLabel(g) {
  if (!g.project_id && !g.site_id) return 'All devices';
  if (g.project_name && g.site_name) return `${g.project_name} / ${g.site_name}`;
  if (g.project_name) return g.project_name;
  return 'Unknown';
}

export default function IotTeamTab() {
  const [grants, setGrants] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [adding, setAdding] = useState(false);

  // Compact add form
  const [newUserId, setNewUserId] = useState('');
  const [newScope, setNewScope] = useState('global'); // 'global' | 'project:ID' | 'site:ID'
  const [newRole, setNewRole] = useState('viewer');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [grantsRes, usersRes, projectsRes] = await Promise.all([
        fetch('/api/iot/team', { headers: bearerHeaders() }),
        fetch('/api/iot/team/eligible-users', { headers: bearerHeaders() }),
        fetch('/api/access/projects'),
      ]);
      setGrants(grantsRes.ok ? (await grantsRes.json()).grants || [] : []);
      setEligibleUsers(usersRes.ok ? (await usersRes.json()).users || [] : []);
      setProjects(projectsRes.ok ? (await projectsRes.json()).projects || [] : []);
    } catch {
      setMsg('Failed to load IoT team data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build scope options: global + one per project + one per site
  const scopeOptions = [{ value: 'global', label: 'All devices' }];
  for (const p of projects) {
    scopeOptions.push({ value: `project:${p.id}`, label: p.name });
    for (const s of (p.sites || [])) {
      scopeOptions.push({ value: `site:${s.id}`, label: `${p.name} / ${s.name}` });
    }
  }

  const parseScope = (scope) => {
    if (scope === 'global') return { projectId: null, siteId: null };
    const [type, id] = scope.split(':');
    if (type === 'project') return { projectId: parseInt(id, 10), siteId: null };
    if (type === 'site') return { projectId: null, siteId: parseInt(id, 10) };
    return { projectId: null, siteId: null };
  };

  const addGrant = async () => {
    if (!newUserId) return;
    setMsg('');
    const { projectId, siteId } = parseScope(newScope);
    try {
      const res = await fetch('/api/iot/team', {
        method: 'POST', headers: bearerHeaders(),
        body: JSON.stringify({ userId: newUserId, projectId, siteId, role: newRole }),
      });
      const j = await res.json();
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setMsg('IoT access granted.');
      setNewUserId(''); setNewScope('global'); setNewRole('viewer');
      setAdding(false);
      load();
    } catch (e) { setMsg(e.message); }
  };

  const removeGrant = async (userId, projectId, siteId) => {
    setMsg('');
    try {
      const params = new URLSearchParams();
      if (projectId) params.set('projectId', projectId);
      if (siteId) params.set('siteId', siteId);
      const res = await fetch(`/api/iot/team/${userId}?${params}`, {
        method: 'DELETE', headers: bearerHeaders(),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setMsg('Access removed.');
      load();
    } catch (e) { setMsg(e.message); }
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  // Group grants by user
  const byUser = {};
  for (const g of grants) {
    if (!byUser[g.user_id]) byUser[g.user_id] = { email: g.user_email, name: g.user_nick_name, grants: [] };
    byUser[g.user_id].grants.push(g);
  }

  return (
    <Box>
      {msg && (
        <Typography variant="body2" sx={{ mb: 2, color: msg.toLowerCase().includes('fail') || msg.toLowerCase().includes('error') ? 'error.main' : 'success.main' }}>
          {msg}
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Users with IoT access can view and manage devices. Scope can be global, per-project, or per-site.
      </Typography>

      {/* Existing grants — compact chip rows grouped by user */}
      {Object.keys(byUser).length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>No IoT access grants yet.</Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {Object.entries(byUser).map(([userId, u]) => (
            <Box key={userId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>{u.email}</Typography>
              {u.grants.map((g, i) => (
                <Chip
                  key={i}
                  size="small"
                  label={`${scopeLabel(g)} · ${g.role}`}
                  color={g.role === 'operator' ? 'primary' : 'default'}
                  variant="outlined"
                  onDelete={() => removeGrant(g.user_id, g.project_id, g.site_id)}
                />
              ))}
            </Box>
          ))}
        </Stack>
      )}

      {/* Add grant — compact inline */}
      {!adding ? (
        <Button size="small" variant="outlined" onClick={() => setAdding(true)}
          startIcon={<Iconify icon="eva:plus-fill" />}>
          Grant IoT access
        </Button>
      ) : (
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Select size="small" value={newUserId} onChange={e => setNewUserId(e.target.value)}
              displayEmpty sx={{ minWidth: 200, fontSize: 13 }}>
              <MenuItem value=""><em>User...</em></MenuItem>
              {eligibleUsers.map(u => (
                <MenuItem key={u.user_id} value={u.user_id} sx={{ fontSize: 13 }}>
                  {u.user_email}
                </MenuItem>
              ))}
            </Select>
            <Select size="small" value={newScope} onChange={e => setNewScope(e.target.value)}
              sx={{ minWidth: 160, fontSize: 13 }}>
              {scopeOptions.map(o => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={newRole} onChange={e => setNewRole(e.target.value)}
              sx={{ minWidth: 100, fontSize: 13 }}>
              <MenuItem value="viewer" sx={{ fontSize: 13 }}>Viewer</MenuItem>
              <MenuItem value="operator" sx={{ fontSize: 13 }}>Operator</MenuItem>
            </Select>
            <Button size="small" variant="contained" onClick={addGrant} disabled={!newUserId}>
              Add
            </Button>
            <IconButton size="small" onClick={() => setAdding(false)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
